// Pure data helpers for the folder-tree explorer shared by the Dashboards,
// Templates, and Reports pages. UI-free so each can be unit-tested without
// React.
//
// Folders and items live in one slash-separated `path` namespace. The tree is
// derived purely from those `path` strings (no parent_id FK in the schema) —
// UNIQUE(path) on the proxy side guarantees a single owner per location. A
// folder occupies its own node; an item is a leaf inside the folder named by
// its path's parent segments. The root has path = "" (empty string) and is
// rendered implicitly by listing its children.

/**
 * Minimum shape every folder/item handled by the tree needs: a stable `id`, a
 * display `name`, and a slash-path. Dashboard/DashboardFolder,
 * Template/TemplateFolder, and Doc/DocFolder/DocTemplate all structurally satisfy
 * this, so each page parameterizes the generics below with its own types.
 */
export interface PathItem {
  readonly id: string
  readonly name: string
  readonly path: string
}

export interface ExplorerTreeNode<F extends PathItem = PathItem, I extends PathItem = PathItem> {
  // The leaf segment of `path`. Empty string for the synthetic root.
  name: string
  // Slash-joined full path, used as the React key and selection identifier.
  // Root is "" (empty string).
  path: string
  // The folder row that owns this node, if any. Intermediate nodes implied by
  // an item path (e.g. "/foo/bar/x" creates "foo" + "foo/bar" if no folder rows
  // declare them) have `folder = undefined`.
  folder?: F
  // Items (dashboards / templates / reports) that live directly inside this
  // folder (i.e. the parent path of their `path` is this node's `path`). Sorted
  // by name for stable rendering.
  items: I[]
  // Child folder nodes, keyed by their leaf segment.
  children: Record<string, ExplorerTreeNode<F, I>>
}

/**
 * Split a path string on "/", trimming and dropping empty segments so
 * "/foo/bar/" and "foo/bar" both yield ["foo", "bar"].
 */
function splitPath(path: string): string[] {
  return path
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

/**
 * Build a combined folder + item tree from two flat lists. Folder rows occupy
 * their own nodes; items land in the node corresponding to their path's parent
 * segments. Intermediate path segments without a folder row are materialized so
 * the tree never has a "missing parent" gap.
 */
export function buildExplorerTree<F extends PathItem = PathItem, I extends PathItem = PathItem>(
  folders: readonly F[],
  items: readonly I[]
): ExplorerTreeNode<F, I> {
  const root: ExplorerTreeNode<F, I> = {
    name: '',
    path: '',
    items: [],
    children: {}
  }

  const ensureNode = (segments: readonly string[]): ExplorerTreeNode<F, I> => {
    let node = root
    let path = ''
    for (const segment of segments) {
      path = path ? `${path}/${segment}` : segment
      let child = node.children[segment]
      if (!child) {
        child = { name: segment, path, items: [], children: {} }
        node.children[segment] = child
      }
      node = child
    }
    return node
  }

  for (const folder of folders) {
    const segments = splitPath(folder.path)
    if (segments.length === 0) continue
    const node = ensureNode(segments)
    node.folder = folder
  }

  for (const item of items) {
    const segments = splitPath(item.path)
    if (segments.length === 0) {
      root.items.push(item)
      continue
    }
    const parentNode = ensureNode(segments.slice(0, -1))
    parentNode.items.push(item)
  }

  sortNode(root)
  return root
}

function sortNode<F extends PathItem, I extends PathItem>(node: ExplorerTreeNode<F, I>): void {
  node.items.sort((a, b) => a.name.localeCompare(b.name))
  for (const k of Object.keys(node.children)) {
    sortNode(node.children[k])
  }
}

/**
 * Depth-first search for a folder node by its full path. Returns null when no
 * folder node with the given path exists.
 */
export function findFolderNode<F extends PathItem, I extends PathItem>(
  root: ExplorerTreeNode<F, I>,
  path: string
): ExplorerTreeNode<F, I> | null {
  if (root.path === path) return root
  for (const k of Object.keys(root.children)) {
    const found = findFolderNode(root.children[k], path)
    if (found) return found
  }
  return null
}

/**
 * Join a folder path and a child name into a unique item / folder path. The
 * child name is slugified — whitespace is collapsed to single dashes,
 * characters outside `[A-Za-z0-9-_.]` are stripped. Returns an empty string when
 * slugifying produces nothing usable (caller should surface a validation error
 * in that case rather than POST it).
 */
export function joinChildPath(parentPath: string, childName: string): string {
  const slug = slugify(childName)
  if (slug === '') return ''
  return parentPath === '' ? slug : `${parentPath}/${slug}`
}

function slugify(input: string): string {
  return input
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9\-_.]/g, '')
}

/**
 * Walk the tree and return every folder path in DFS order, including
 * intermediate ("implicit") folders that exist only as path segments for items.
 * Root is always included as the empty string. Used by the parent-folder picker
 * so users can place a new item anywhere in the tree, even under an implicit
 * folder.
 */
export function listAllFolderPaths<F extends PathItem, I extends PathItem>(
  root: ExplorerTreeNode<F, I>
): string[] {
  const out: string[] = ['']
  const walk = (node: ExplorerTreeNode<F, I>): void => {
    for (const k of Object.keys(node.children)) {
      const child = node.children[k]
      out.push(child.path)
      walk(child)
    }
  }
  walk(root)
  return out
}

/**
 * Return a new expanded-set that includes every ancestor of `path` (and `path`
 * itself, if non-empty), so the tree opens to reveal a just-created item. A
 * no-op (returns `prev`) for the root path. Used by the explorer pages to reveal
 * freshly created folders/items.
 */
export function expandAncestors(prev: Set<string>, path: string): Set<string> {
  if (path === '') return prev
  const next = new Set(prev)
  let acc = ''
  for (const seg of path.split('/')) {
    acc = acc === '' ? seg : `${acc}/${seg}`
    next.add(acc)
  }
  return next
}

/** Parent path of a slash path. Root children return "". */
export function parentPathOf(path: string): string {
  const i = path.lastIndexOf('/')
  return i >= 0 ? path.slice(0, i) : ''
}

/** Last segment of a slash path. */
export function leafSegment(path: string): string {
  const i = path.lastIndexOf('/')
  return i >= 0 ? path.slice(i + 1) : path
}

/** Join a parent folder path with an existing leaf segment (no slugify). */
export function joinUnderParent(parentPath: string, leaf: string): string {
  if (leaf === '') return parentPath
  return parentPath === '' ? leaf : `${parentPath}/${leaf}`
}

/**
 * True when `prefix` is the same path as `candidate` or one of its
 * ancestors. Treats segments as whole units so `foo` is not a prefix
 * of `foobar`.
 */
export function isPathPrefix(prefix: string, candidate: string): boolean {
  if (prefix === '') return true
  if (prefix === candidate) return true
  return candidate.startsWith(`${prefix}/`)
}

/** Rewrite `path` after a folder prefix move from `fromPath` to `toPath`. */
export function relocateUnderPrefix(fromPath: string, toPath: string, path: string): string {
  if (path === fromPath) return toPath
  if (path.startsWith(`${fromPath}/`)) return toPath + path.slice(fromPath.length)
  return path
}

/**
 * Count dashboards and descendant folders (including implicit path
 * segments) under `folderPath`. The folder itself is not counted.
 */
export function countPrefixDescendants(
  folderPath: string,
  folders: readonly PathItem[],
  items: readonly PathItem[]
): number {
  if (folderPath === '') return 0
  const prefix = `${folderPath}/`
  const paths = new Set<string>()
  for (const folder of folders) {
    if (folder.path.startsWith(prefix)) paths.add(folder.path)
  }
  for (const item of items) {
    if (!item.path.startsWith(prefix)) continue
    paths.add(item.path)
    let acc = ''
    const segs = item.path.split('/')
    for (let i = 0; i < segs.length - 1; i++) {
      acc = acc === '' ? segs[i] : `${acc}/${segs[i]}`
      if (acc.startsWith(prefix)) paths.add(acc)
    }
  }
  return paths.size
}

function nodeDisplayName<F extends PathItem, I extends PathItem>(
  node: ExplorerTreeNode<F, I>
): string {
  return node.folder?.name ?? node.name
}

function nodeMatchesFilter<F extends PathItem, I extends PathItem>(
  node: ExplorerTreeNode<F, I>,
  q: string
): boolean {
  return nodeDisplayName(node).toLowerCase().includes(q) || node.path.toLowerCase().includes(q)
}

function itemMatchesFilter<I extends PathItem>(item: I, q: string): boolean {
  return item.name.toLowerCase().includes(q) || item.path.toLowerCase().includes(q)
}

/**
 * Keep matching folders/items plus the ancestors needed to show them.
 * Non-matching siblings are dropped. An empty/whitespace filter is a no-op.
 */
export function filterExplorerTree<F extends PathItem = PathItem, I extends PathItem = PathItem>(
  root: ExplorerTreeNode<F, I>,
  filter: string
): ExplorerTreeNode<F, I> {
  const q = filter.trim().toLowerCase()
  if (q === '') return root

  const filterNode = (node: ExplorerTreeNode<F, I>): ExplorerTreeNode<F, I> | null => {
    const children: Record<string, ExplorerTreeNode<F, I>> = {}
    for (const key of Object.keys(node.children)) {
      const child = filterNode(node.children[key])
      if (child) children[key] = child
    }
    const items = node.items.filter((item) => itemMatchesFilter(item, q))
    const selfMatch = node.path !== '' && nodeMatchesFilter(node, q)
    if (!selfMatch && items.length === 0 && Object.keys(children).length === 0) {
      return null
    }
    return { ...node, items, children }
  }

  return filterNode(root) ?? { ...root, items: [], children: {} }
}

/** One row from a depth-first walk of a folder tree (root omitted). */
export type FlatExplorerRow<F extends PathItem = PathItem, I extends PathItem = PathItem> =
  | { kind: 'folder'; node: ExplorerTreeNode<F, I> }
  | { kind: 'item'; item: I }

/**
 * Flatten a tree into folder + item rows in DFS order. Used by drill-down
 * search so filterExplorerTree can stay nested while the sidebar renders a
 * path-labeled list.
 */
export function flattenExplorerTree<F extends PathItem = PathItem, I extends PathItem = PathItem>(
  root: ExplorerTreeNode<F, I>
): FlatExplorerRow<F, I>[] {
  const out: FlatExplorerRow<F, I>[] = []
  const walk = (node: ExplorerTreeNode<F, I>): void => {
    if (node.path !== '') out.push({ kind: 'folder', node })
    for (const item of node.items) out.push({ kind: 'item', item })
    for (const key of Object.keys(node.children)) {
      walk(node.children[key])
    }
  }
  walk(root)
  return out
}

/**
 * Reorder a node's child keys so `pinnedPaths` appear first (in the order
 * given), then the remaining children in their existing insertion order.
 * Unknown pinned paths are skipped.
 */
export function orderPinnedChildKeys(
  children: Record<string, { path: string }>,
  pinnedPaths: readonly string[]
): string[] {
  const keys = Object.keys(children)
  if (pinnedPaths.length === 0) return keys
  const pinned: string[] = []
  const seen = new Set<string>()
  for (const path of pinnedPaths) {
    const key = keys.find((k) => children[k].path === path)
    if (key !== undefined && !seen.has(key)) {
      pinned.push(key)
      seen.add(key)
    }
  }
  return [...pinned, ...keys.filter((k) => !seen.has(k))]
}

/** Every folder path at or under `folderPath`, including implicit segments. */
export function subtreeFolderPaths(
  folderPath: string,
  folders: readonly PathItem[],
  items: readonly PathItem[]
): Set<string> {
  const out = new Set<string>([folderPath])
  if (folderPath === '') return out
  const prefix = `${folderPath}/`
  for (const folder of folders) {
    if (folder.path === folderPath || folder.path.startsWith(prefix)) out.add(folder.path)
  }
  for (const item of items) {
    if (!item.path.startsWith(prefix)) continue
    let acc = ''
    const segs = item.path.split('/')
    for (let i = 0; i < segs.length - 1; i++) {
      acc = acc === '' ? segs[i] : `${acc}/${segs[i]}`
      if (acc === folderPath || acc.startsWith(prefix)) out.add(acc)
    }
  }
  return out
}
